import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Top-level error boundary for the account portal.
 *
 * A single unhandled render throw anywhere in the tree (e.g. a device where
 * `localStorage` access throws — iOS Safari "Block All Cookies" / partitioned
 * in-app webviews) would otherwise unmount the whole app and leave a blank white
 * screen on the sign-in front door. This catches it and shows a branded, useful
 * fallback (reload + the Avery support line) so the door is never a dead page.
 *
 * Wraps <App/> in main.tsx. Class component because React error boundaries have
 * no hook equivalent.
 */

// Avery's live support line (same as @parkify/ui SupportButton) — reachable even
// when the app itself failed to render.
const SUPPORT_E164 = '+18559175072'
const SUPPORT_PRETTY = '1 (855) 917-5072'

interface State {
  failed: boolean
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface it so a fallback is never silent/undiagnosable in the field.
    console.error('[portal] unhandled render error — showing fallback:', error, info)
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children
    return (
      <div style={{
        minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 18,
        padding: '32px 20px', textAlign: 'center',
      }}>
        <img src="/parkify-logo.svg" alt="Parkify" height={26} style={{ display: 'block' }} />
        <div style={{ maxWidth: 380 }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px', color: 'var(--color-black)' }}>
            Something went wrong
          </h1>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--color-gray-700)' }}>
            We couldn't load your account just now. Please reload — if it keeps
            happening, we're one text or call away.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 300 }}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 18px', borderRadius: 'var(--radius-full)', border: 'none',
              background: 'var(--color-brand)', color: 'var(--color-white)',
              fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Reload
          </button>
          <a
            href={`sms:${SUPPORT_E164}?&body=${encodeURIComponent('Hi Avery — I need help signing in.')}`}
            style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-brand)', textDecoration: 'none' }}
          >
            Text us
          </a>
          <a
            href={`tel:${SUPPORT_E164}`}
            style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-600)', textDecoration: 'none' }}
          >
            or call {SUPPORT_PRETTY}
          </a>
        </div>
      </div>
    )
  }
}
